import { useCallback, useState } from 'react';
import {
  Flex,
  RemoteImage,
  Typography,
  ColorValues,
  Carousel,
} from '@banhala/ui-kit';
import type { RollingBannerListProps, RollingBannerItem } from './types';
import { isWeb } from '../../../../utils';
import { useLogClickBanner } from './hooks/useLogClickBanner';
import { useLogDisplayBanner } from './hooks/useLogDisplayBanner';
import { useSharedValue } from 'react-native-reanimated';
import { useGuardedScreenWidth, useOpenDeeplink } from '../../../../hooks';

/**
 * parallax carousel — body.item.view_type === 'SLIDE_QUEUE' 케이스.
 * 정사각형 배너를 좌우 인접 아이템과 함께 노출.
 */
export const SlideQueueBanner = ({
  entity,
  logging,
  logDisabled,
  isVisible = true,
}: RollingBannerListProps) => {
  const screenWidth = useGuardedScreenWidth('SlideQueueBanner');
  const { openDeeplink } = useOpenDeeplink();
  const { logClickBannerItem } = useLogClickBanner(logging);

  const [currentIndex, setCurrentIndex] = useState(0);
  const progress = useSharedValue<number>(0);

  useLogDisplayBanner({ entity, logging, currentIndex });

  const handleBannerPress = useCallback(
    (item: RollingBannerItem) => {
      if (!item.item.deeplink) return;
      logClickBannerItem(item.logging);
      openDeeplink(item.item.deeplink);
    },
    [openDeeplink, logClickBannerItem],
  );

  const onProgressChange = useCallback(
    (_: number, absoluteProgress: number) => {
      progress.value = absoluteProgress;
      const newIndex = Math.min(
        entity.length - 1,
        Math.round(absoluteProgress),
      );
      if (newIndex !== currentIndex) {
        setCurrentIndex(newIndex);
      }
    },
    [currentIndex, entity.length, progress],
  );

  if (entity.length === 0) return null;
  if (!screenWidth) return null;

  return (
    <Flex position={'relative'} padding={{ top: isWeb ? 12 : 0 }}>
      <Carousel
        data={entity}
        onProgressChange={onProgressChange}
        mode="parallax"
        autoPlay={isVisible && !logDisabled}
        autoPlayInterval={3000}
        modeConfig={{
          parallaxScrollingScale: 0.9,
          parallaxScrollingOffset: 78,
          parallaxAdjacentItemScale: 0.76,
        }}
        renderItem={({ item: bannerItem, index }) => {
          return (
            <Flex.Center
              key={index}
              onPress={() => handleBannerPress(bannerItem)}
            >
              <RemoteImage
                uri={bannerItem.item.image_url}
                width={screenWidth * 0.91}
                height={screenWidth * 0.91}
                borderRadius={'xlarge'}
              />
            </Flex.Center>
          );
        }}
        vertical={false}
        width={screenWidth}
        height={screenWidth * 0.91}
      />
      <Flex.Row
        position={'absolute'}
        bottom={32}
        right={48}
        borderRadius={20}
        backgroundColorValue={ColorValues.alpha_dark12}
        padding={{ horizontal: 6, vertical: 4 }}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Typography type={'body4'} color={'content_always_white'}>
          {currentIndex + 1}
        </Typography>
        <Flex
          width={1}
          height={8}
          backgroundColor={'white'}
          margin={{ horizontal: 4 }}
        />
        <Typography type={'body4'} rawColor={`#FFFFFF99`}>
          {entity.length}
        </Typography>
      </Flex.Row>
    </Flex>
  );
};
